import { FindingList } from 'components/ai/FindingList';
import { ArrowRightIcon, CircleAlert, ClipboardCheckIcon, PlusIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useId, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import type { FigureDefinition, FigureFindingsGroup, FigureFindingsItem, FigureRowsColumn } from 'types/aiChatFigure';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { dateStringToDateObject } from 'utils/parseDate';
import { FigureCellValue } from './FigureCellValue';
import { FigureCopyButton } from './FigureCopyButton';
import { FigureFrame } from './FigureFrame';
import { inlineTextCell, recordOf } from './figureRows';
import { ShowMoreButton } from './FigureTable';

// The first ten items show; "Show more" opens the rest under them, as a list's rows.
const ITEMS = 10;

const TEXT_COLUMN: FigureRowsColumn = { field: 'text', label: 'text', type: 'text' };
const HEADING_CLASS_NAME = 'text-caption text-foreground-tertiary';
const TEXT_CLASS_NAME = 'text-meta m-0 break-words whitespace-pre-wrap';
const NONE_CLASS_NAME = 'text-meta text-foreground-tertiary m-0 italic';
const LIST_HEADING_CLASS_NAME = 'text-caption font-semibold';

// One record link inside a short text: `by [Mari Tamm](crespect://employee/900211)`. Its label may hold one level of
// brackets, as a contact's number or a case name does: `[[1187] Lumen Grid OÜ](crespect://contact/901187)`.
const RECORD_LINK = /\[(?:[^[\]]|\[[^\]]*\])*\]\([^()\s]+\)/;

/**
 * A short text holding at most one record link: the link as its chip, the text around it as text. A link this build
 * cannot open reads as its label, as it does in the answer's text.
 */
function LinkedText({ text, hitArea }: { text: string; hitArea?: boolean }) {
    const link = RECORD_LINK.exec(text);
    if (!link) {
        return <>{text}</>;
    }
    const cell = inlineTextCell(link[0]);
    return (
        <>
            {text.slice(0, link.index)}
            {recordOf(cell) ? (
                <FigureCellValue cell={cell} column={TEXT_COLUMN} chipClassName="max-w-full" hitArea={hitArea} />
            ) : (
                link[0].slice(1, link[0].lastIndexOf(']('))
            )}
            {text.slice(link.index + link[0].length)}
        </>
    );
}

interface Labels {
    original: string;
    proposal: string;
    copy: string;
}

/**
 * One record's finding: its chip, date and detail; its text as stored beside the AI's proposal, side by side from 36rem
 * and stacked below; then what the proposal changed, or what the text lacks when there is none, and what to still do.
 */
function FindingsItem({ item, labels }: { item: FigureFindingsItem; labels: Labels }) {
    const intl = useIntl();
    const proposal = item.proposal?.trim() ? item.proposal : null;
    // Each Copy names the record it copies for: a card holds up to 50 of them.
    const recordName = recordOf(inlineTextCell(item.record))?.label;
    const findings = item.findings ?? [];
    const consider = item.consider ?? [];
    return (
        <li className="border-divider-soft flex flex-col gap-2 border-t px-4 pt-2.5 pb-3">
            <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                <span className="flex max-w-full min-w-0">
                    <LinkedText text={item.record} />
                </span>
                {item.date || item.detail ? (
                    <span className="text-caption text-foreground-tertiary min-w-0 break-words">
                        {item.date ? <FormattedDate value={dateStringToDateObject(item.date)} /> : null}
                        {item.date && item.detail ? ' · ' : null}
                        {/* The row wraps on a narrow card: an extended hit area would cover the chip above it. */}
                        {item.detail ? <LinkedText text={item.detail} hitArea={false} /> : null}
                    </span>
                ) : null}
            </div>
            <div className="grid gap-x-6 gap-y-2 @xl:grid-cols-2">
                <div className="flex min-w-0 flex-col gap-1">
                    {/* Side by side, the heading takes the Copy row's height, so both texts start level. */}
                    <span className={cn(HEADING_CLASS_NAME, '@xl:flex @xl:min-h-8 @xl:items-center')}>
                        {labels.original}
                    </span>
                    {/* Side by side, the original takes the proposal box's inset, so both first lines align. */}
                    {item.original.trim() ? (
                        <p className={cn(TEXT_CLASS_NAME, 'text-foreground-secondary', proposal && '@xl:pt-2')}>
                            {item.original}
                        </p>
                    ) : (
                        <p className={cn(NONE_CLASS_NAME, proposal && '@xl:pt-2')}>
                            {intl.formatMessage({ id: 'ai-chat.figure.findings.empty' })}
                        </p>
                    )}
                </div>
                <div className="flex min-w-0 flex-col gap-1">
                    <div className="flex flex-wrap items-center justify-between gap-x-2 @xl:min-h-8">
                        <span className={HEADING_CLASS_NAME}>{labels.proposal}</span>
                        {proposal ? (
                            <FigureCopyButton
                                label={labels.copy}
                                accessibleName={recordName ? `${labels.copy}, ${recordName}` : undefined}
                                copy={() => navigator.clipboard.writeText(proposal)}
                            />
                        ) : null}
                    </div>
                    {proposal ? (
                        <p
                            className={cn(
                                TEXT_CLASS_NAME,
                                'bg-ai-soft/40 new:bg-ai-wash text-foreground rounded-md px-3 py-2'
                            )}
                        >
                            {proposal}
                        </p>
                    ) : (
                        <p className={NONE_CLASS_NAME}>
                            {intl.formatMessage({ id: 'ai-chat.figure.findings.no-proposal' })}
                        </p>
                    )}
                </div>
            </div>
            {findings.length ? (
                <div className="flex flex-col gap-1">
                    <span
                        className={cn(
                            LIST_HEADING_CLASS_NAME,
                            proposal
                                ? 'text-success-darker new:text-success-ink'
                                : 'text-warning-darker new:text-warning-ink'
                        )}
                    >
                        {intl.formatMessage({
                            id: proposal
                                ? 'timecards.form.description-suggestion-changes'
                                : 'timecards.form.description-needs-detail',
                        })}
                    </span>
                    <FindingList
                        rules={findings}
                        Marker={proposal ? ArrowRightIcon : PlusIcon}
                        markerClassName={proposal ? 'text-success new:text-success-ink' : 'text-warning'}
                    />
                </div>
            ) : null}
            {consider.length ? (
                <div className="flex flex-col gap-1">
                    <span className={cn(LIST_HEADING_CLASS_NAME, 'text-warning-darker new:text-warning-ink')}>
                        {intl.formatMessage({ id: 'timecards.form.description-suggestion-consider' })}
                    </span>
                    <FindingList rules={consider} Marker={CircleAlert} markerClassName="text-warning" />
                </div>
            ) : null}
        </li>
    );
}

interface Props {
    definition: FigureDefinition;
}

interface GroupsProps {
    groups: FigureFindingsGroup[];
    /** The base of the ids that name each group's section by its band. */
    idBase: string;
    labels: Labels;
    /** The items to draw, by their place across the groups. */
    from: number;
    to: number;
}

/**
 * The groups' items from `from` to `to`, each under its group's header: its record and what the review found across
 * it. A group the cut began earlier carries on without its header, straight under the item before.
 */
function FindingsGroups({ groups, idBase, labels, from, to }: GroupsProps) {
    let start = 0;
    return groups.map((group, index) => {
        const first = start;
        start += group.items.length;
        const items = group.items.slice(Math.max(from - first, 0), Math.max(to - first, 0));
        if (!items.length) {
            return null;
        }
        return (
            // The groups never reorder in place: a new review is a new Figure. A group the cut began earlier is named by its band above the cut.
            <section key={index} aria-labelledby={`${idBase}-${index}`}>
                {first >= from ? (
                    <div className="border-divider-soft bg-surface-subtle flex flex-col gap-1.5 border-t px-4 py-2.5">
                        <span
                            id={`${idBase}-${index}`}
                            className="text-meta text-foreground flex max-w-full min-w-0 font-semibold"
                        >
                            <LinkedText text={group.record} />
                        </span>
                        {group.notes?.length ? (
                            <FindingList
                                rules={group.notes.map((reason) => ({ reason }))}
                                Marker={CircleAlert}
                                markerClassName="text-warning"
                            />
                        ) : null}
                    </div>
                ) : null}
                <ul className="m-0 list-none p-0">
                    {items.map((item, offset) => (
                        <FindingsItem key={offset} item={item} labels={labels} />
                    ))}
                </ul>
            </section>
        );
    });
}

export function FindingsFigure({ definition }: Props) {
    const intl = useIntl();
    const [open, setOpen] = useState(false);
    const idBase = useId();
    const groups = definition.inline?.groups ?? [];
    const items = groups.flatMap((group) => group.items);
    const labels: Labels = {
        original: definition.labels?.original ?? intl.formatMessage({ id: 'ai-chat.figure.findings.original' }),
        proposal: definition.labels?.proposal ?? intl.formatMessage({ id: 'ai-chat.figure.findings.proposal' }),
        copy: definition.labels?.proposal
            ? intl.formatMessage({ id: 'ai-chat.figure.findings.copy-labelled' }, { label: definition.labels.proposal })
            : intl.formatMessage({ id: 'ai-chat.figure.findings.copy' }),
    };
    const timecards = items.every((item) => recordOf(inlineTextCell(item.record))?.entityType === 'TIMECARD');
    const meta = (
        <>
            {intl.formatMessage(
                { id: timecards ? 'ai-chat.progress.chip.searchTimecards' : 'ai-chat.figure.findings.items' },
                { count: items.length }
            )}
            {definition.asOf ? (
                <>
                    {' · '}
                    <FormattedMessage
                        id="ai-chat.figure.as-of"
                        values={{ date: <FormattedDate value={dateStringToDateObject(definition.asOf)} /> }}
                    />
                </>
            ) : null}
        </>
    );

    return (
        <FigureFrame
            icon={ClipboardCheckIcon}
            tileClassName={NEUTRAL_TILE_CLASS}
            title={definition.title}
            meta={meta}
            className="my-3"
        >
            <FindingsGroups groups={groups} idBase={idBase} labels={labels} from={0} to={ITEMS} />
            {items.length > ITEMS ? (
                <div className="t-acc" data-open={open ? 'true' : 'false'}>
                    <div className="t-acc-panel">
                        <div className="t-acc-panel-inner">
                            <FindingsGroups
                                groups={groups}
                                idBase={idBase}
                                labels={labels}
                                from={ITEMS}
                                to={items.length}
                            />
                        </div>
                    </div>
                    <div className="border-divider-soft flex border-t px-4 py-2.5">
                        <ShowMoreButton open={open} more={items.length - ITEMS} onToggle={() => setOpen(!open)} />
                    </div>
                </div>
            ) : null}
        </FigureFrame>
    );
}
